import { Icon } from 'components/Icon';
import { classNames, React, useEffect, useRef, useState } from 'libs/react';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelect.scss';
/**
 * @component MultiSelect
 * @since 1.0.0
 * @hidden
 */
export function MultiSelect(props) {
    const { name, values, options, required, disabled, placeholder } = Object.assign({ placeholder: '', values: [] }, props);
    const [open, setOpen] = useState(false);
    const [overlayLayout, setOverlayLayout] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const overlayContainerRef = useRef(null);
    /**
     * @function onBlur
     * @since 1.0.0
     * @hidden
     */
    function onBlur() {
        if (props.onBlur) {
            props.onBlur();
        }
    }
    /**
     * @function onFocus
     * @since 1.0.0
     * @hidden
     */
    function onFocus() {
        if (props.onFocus) {
            props.onFocus();
        }
    }
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function onChange(value) {
        let newValues = [];
        if (values.find((val) => val === value)) {
            newValues = values.filter((val) => val !== value);
        }
        else {
            newValues = [...values, value];
        }
        //  validate that values are in options
        newValues = newValues.filter((val) => options.find((option) => option.value === val) != null);
        if (props.onChange) {
            props.onChange(newValues);
        }
    }
    /**
     * @function toggleCheckboxes
     * @since 1.0.0
     * @hidden
     */
    const toggleCheckboxes = () => {
        setOpen(!open);
    };
    /**
     * @function handleCloseOverlay
     * @since 1.0.0
     * @hidden
     */
    const handleCloseOverlay = () => {
        setOpen(false);
        onBlur();
    };
    const optionsFormatted = useMemo(() => options.map((option) => {
        var _a;
        const checked = (_a = values.find((val) => val === option.value)) !== null && _a !== void 0 ? _a : false;
        return Object.assign(Object.assign({}, option), { checked: Boolean(checked) });
    }), [values, options]);
    useEffect(() => {
        if (!open) {
            setOverlayLayout(null);
            return;
        }
        function updateOverlayLayout() {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const padding = 16;
            const spaceBelow = window.innerHeight - rect.bottom - padding;
            const maxHeight = Math.min(300, Math.max(spaceBelow, 120));
            setOverlayLayout({
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
                maxHeight
            });
        }
        updateOverlayLayout();
        window.addEventListener('resize', updateOverlayLayout);
        window.addEventListener('scroll', updateOverlayLayout, true);
        return () => {
            window.removeEventListener('resize', updateOverlayLayout);
            window.removeEventListener('scroll', updateOverlayLayout, true);
        };
    }, [open]);
    useEffect(() => {
        if (!open) {
            return;
        }
        function handleMouseDown(event) {
            var _a, _b;
            const target = event.target;
            if (((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
                ((_b = overlayContainerRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))) {
                return;
            }
            handleCloseOverlay();
        }
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [open]);
    const inputValue = useMemo(() => {
        return optionsFormatted
            .filter((option) => option.checked)
            .map((option) => option.label)
            .join(', ');
    }, [optionsFormatted]);
    const overlay = open && overlayLayout ? (React.createElement("div", { className: "multi-select-overlay-container", "data-closeable-ignore": true, style: {
            top: overlayLayout.top,
            left: overlayLayout.left,
            width: overlayLayout.width
        } },
        React.createElement("div", { className: "multi-select-overlay", ref: overlayContainerRef, style: { maxHeight: overlayLayout.maxHeight } }, optionsFormatted.map((option, index) => (React.createElement("div", { key: `option-${index}`, className: "multi-select-checkbox-container", onClick: () => onChange(option.value) },
            option.checked ? (React.createElement(Icon, { value: "fas square-check", className: "multi-select-checkbox-icon" })) : (React.createElement(Icon, { value: "square", className: "multi-select-uncheck-icon" })),
            React.createElement("span", null, option.label))))))) : null;
    return (React.createElement("div", { className: classNames('multi-select-container', {
            'multi-select-container--open': open
        }), ref: containerRef },
        React.createElement("div", { className: "multi-select-input-container" },
            React.createElement("input", { type: "text", readOnly: true, name: name, value: inputValue, onClick: toggleCheckboxes, onFocus: onFocus, ref: inputRef, placeholder: placeholder, required: required, disabled: disabled })),
        overlay && createPortal(overlay, document.body)));
}
